import React from "react";

import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useSelector, useDispatch } from "react-redux";
import BasicLoading from "ZeroRuby/BasicLoading";

import { SurfaceHeader, SurfaceSection } from "Cobalt/Surface";
import AgentHeader from "Common/Agent/AgentHeader";
import { formFormatDate, formParseDate } from "Common/forms/utils";
import { Row } from "Common/Grid";
import { usePartnerContext } from "Common/PartnerContext";
import ScrollToTopOnMount from "Common/ScrollToTopOnMount";

import { stepCompleted } from "./actions";
import { useGetApplicationQuery, useUpdateEmployeeFormMutation, getEmployeeForm, getCompanyProfileID } from "./api";
import {
  PhysicalAddress,
  AddressCity,
  AddressState,
  AddressZipCode,
  HomePhoneNumber,
  WorkPhoneNumber,
  Gender,
  MaritalStatus,
  FirstName,
  MiddleInitial,
  LastName,
  BornAt,
  SalaryAmount,
  TobaccoUser,
} from "./FinalFormQuestions";
import FooterNavigation from "./FooterNavigation";
import Page from "./Page";

const focusOnErrors = createDecorator();

const introText = (isRenewal) => {
  return `I'm Leigh, your virtual insurance specialist. ${isRenewal ? "Renewing" : "Signing up for"} health insurance with SimplyInsured takes only a few minutes. Let's get started.  Even if you don't want insurance, we need some information from you in order to decline insurance.`;
};

export const BasicInformationPage = ({ isRenewal, availablePlans }) => {
  const { isLoading } = useGetApplicationQuery();

  return (
    <Page title="Contact Information">
      <ScrollToTopOnMount />
      {isLoading ? <BasicLoading /> : <BasicInformationForm isRenewal={isRenewal} availablePlans={availablePlans} />}
    </Page>
  );
};

const formFormatEmployeeForm = (employee: EmployeeForm | null | undefined) =>
  employee
    ? {
        ...employee,
        bornAt: employee.bornAt ? formFormatDate(new Date(employee.bornAt)) : null,
      }
    : {};

const formParseEmployeeForm = (values: any) => ({
  ...values,
  bornAt: formParseDate(values.bornAt),
});

const BasicInformationForm = ({ isRenewal, availablePlans }) => {
  const { syncedEmployeeFields } = usePartnerContext();
  const [updateEmployeeForm] = useUpdateEmployeeFormMutation();
  const dispatch = useDispatch();

  const handleUpdateEmployeeForm = async (values) => {
    const result = await updateEmployeeForm(formParseEmployeeForm(values)).unwrap();
    if (result) {
      dispatch(stepCompleted("basicInformation"));
    }
  };

  const salaryPlanTypes = ["life", "term_life", "whole_life", "long_term_disability", "short_term_disability"];
  const offeredSalaryRelatedPlans = salaryPlanTypes.some((planType) => availablePlans.includes(planType));

  const employeeForm = useSelector(getEmployeeForm);
  const initialValues = {
    ...(formFormatEmployeeForm(employeeForm) || {}),
  };

  const companyProfileID = useSelector(getCompanyProfileID);
  // showTobaccoUser is a special case for Granite Subsidiary Microbiologics
  // which has different contributions based on smoking
  const showTobaccoUser = companyProfileID && companyProfileID === "586969";

  const addressDataComplete = employeeForm.homeAddress && employeeForm.city && employeeForm.state && employeeForm.zipCode;

  return (
    <Form
      initialValues={initialValues}
      decorators={[focusOnErrors]}
      onSubmit={(values) => {
        handleUpdateEmployeeForm(values);
      }}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <SurfaceHeader>Your Health Insurance</SurfaceHeader>
          <SurfaceSection>
            <AgentHeader>{introText(isRenewal)}</AgentHeader>
          </SurfaceSection>
          <SurfaceSection title="Contact Information">
            {/* if the employee is missing data for any name field, show all of them */}
            {!(syncedEmployeeFields.fullName && employeeForm.firstName && employeeForm.lastName) && (
              <Row>
                <FirstName sm={4} xs={8} />
                <MiddleInitial sm={2} xs={4} />
                <LastName sm={4} />
              </Row>
            )}
            {!(syncedEmployeeFields.physicalAddress && addressDataComplete) && (
              <>
                <Row>
                  <PhysicalAddress sm={6} />
                </Row>
                <Row>
                  <AddressCity sm={4} />
                  <AddressState sm={3} xs={8} />
                  <AddressZipCode sm={2} xs={4} />
                </Row>
              </>
            )}

            {!(syncedEmployeeFields.homePhoneNumber && employeeForm.homePhoneNumber) && (
              <Row>
                <HomePhoneNumber sm={4} />
              </Row>
            )}

            {!(syncedEmployeeFields.workPhoneNumber && employeeForm.workPhoneNumber) && (
              <Row>
                <WorkPhoneNumber sm={4} />
              </Row>
            )}
          </SurfaceSection>
          <SurfaceSection title="Demographic Information">
            {!(syncedEmployeeFields.bornAt && employeeForm.bornAt) && (
              <Row>
                <BornAt sm={4} />
              </Row>
            )}

            <Row>
              {!(syncedEmployeeFields.gender && employeeForm.gender) && <Gender />}
              {!(syncedEmployeeFields.maritalStatus && employeeForm.maritalStatus) && <MaritalStatus />}
              {offeredSalaryRelatedPlans && !employeeForm.salaryAmount && <SalaryAmount sm={4} />}
              {showTobaccoUser && <TobaccoUser />}
            </Row>
          </SurfaceSection>
          <FooterNavigation noBack />
        </form>
      )}
    </Form>
  );
};

export default BasicInformationPage;
